import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';

import { Capacitor } from '@capacitor/core';
import { type BackButtonEvent, useIonAlert, useIonRouter } from '@ionic/react';

import { isNative } from '../helpers/utils.helper';
import { JOURNAL_ALERT, showAlert } from '../pages/Journal/journalSlice';
import { logout } from '../pages/Login/Login.service';
import { TABS_MODAL } from '../pages/tabs/tabsSlice';
import { useAppDispatch, useAppSelector } from '../store';
import { useCapturePostHog } from './useCapturePostHog';

export const useBackButtonListener = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [presentAlert] = useIonAlert();
  const dispatch = useAppDispatch();
  const ionRouter = useIonRouter();
  const { capturePostHogCustomEvent } = useCapturePostHog();
  const modal = useAppSelector((state) => state.tabs.modal);
  const token = useAppSelector((state) => state.login.authResult.token);
  const stepsRef = useRef<string[]>([]);
  const hasToken = Boolean(token);

  useEffect(() => {
    if (stepsRef.current.length === 0 && location.pathname !== '/') {
      stepsRef.current.push(location.pathname);
    }
  }, [location.pathname]);

  useEffect(() => {
    const unlisten = history.listen((location, action) => {
      switch (action) {
        case 'PUSH':
          stepsRef.current.push(location.pathname);
          break;
        case 'REPLACE':
          stepsRef.current.pop();
          stepsRef.current.push(location.pathname);
          break;
      }
    });

    return () => {
      unlisten();
    };
  }, [history]);

  useEffect(() => {
    const backButtonListener: EventListener = (event) => {
      const ev = event as BackButtonEvent;

      // 100 is ion-modal priority level
      ev.detail.register(100, (processNextHandler) => {
        if (modal === TABS_MODAL.CHECKIN) {
          dispatch(showAlert(JOURNAL_ALERT.LEAVE_JOURNAL));
        } else {
          processNextHandler();
        }
      });
    };

    if (isNative()) {
      document.addEventListener('ionBackButton', backButtonListener);
    }

    return () => {
      document.removeEventListener('ionBackButton', backButtonListener);
    };
  }, [dispatch, modal]);

  useEffect(() => {
    const backButtonListener: EventListener = (event) => {
      const ev = event as BackButtonEvent;

      // 10 is navigation priority level
      ev.detail.register(10, async () => {
        if (!ionRouter.canGoBack()) {
          if (Capacitor.isPluginAvailable('App')) {
            const { App } = await import('@capacitor/app');
            App.exitApp();
          }
        } else if (stepsRef.current.at(-2) === '/login' && hasToken) {
          presentAlert({
            header: t('preferencesPage.logout'),
            message: t('preferencesPage.logoutMessage'),
            buttons: [
              {
                text: t('preferencesPage.cancel'),
                role: 'cancel',
                cssClass: 'secondary',
              },
              {
                text: t('preferencesPage.ok'),
                handler: async () => {
                  capturePostHogCustomEvent('useBackButtonListener logging out');
                  await logout(dispatch);
                  stepsRef.current.pop();
                  history.goBack();
                },
              },
            ],
          });
        } else {
          stepsRef.current.pop();
          history.goBack();
        }
      });
    };

    if (isNative()) {
      document.addEventListener('ionBackButton', backButtonListener);
    }

    return () => {
      document.removeEventListener('ionBackButton', backButtonListener);
    };
  }, [capturePostHogCustomEvent, dispatch, hasToken, history, ionRouter, presentAlert, t]);
};
